import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCheck, Loader2, MessageCircle, RefreshCw, Search, Send, Trash2, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AdminCard, Pill, TableWrap, Th, Td, inputCls } from "@/pages/admin/ui";

const STATUS = {
  sent: { label: "Envoyé", tone: "blue" },
  queued: { label: "En file", tone: "slate" },
  accepted: { label: "Accepté", tone: "slate" },
  sending: { label: "Envoi…", tone: "slate" },
  delivered: { label: "Livré", tone: "green" },
  read: { label: "Lu", tone: "green" },
  failed: { label: "Échec", tone: "rose" },
  undelivered: { label: "Non livré", tone: "rose" },
  skipped: { label: "Ignoré", tone: "amber" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function AdminWhatsAppLogs() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/whatsapp-logs", { params: { status, q, limit: 200 } });
      setData(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [status, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    const iv = setInterval(() => { load(); }, 8000);
    return () => clearInterval(iv);
  }, [load]);

  const refreshStatuses = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/whatsapp-logs/refresh");
      toast.success(`${data.updated} statut(s) mis à jour sur ${data.checked} vérifié(s)`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Effacer tout l'historique des envois WhatsApp ?")) return;
    try { await api.delete("/admin/whatsapp-logs"); toast.success("Historique effacé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const [testing, setTesting] = useState(false);
  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/whatsapp-test");
      toast.success(`Message de test envoyé à ${data.masked || data.to} ✅ Vérifiez votre WhatsApp.`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
  };

  const s = data?.stats || {};
  const ok = (s.sent || 0) + (s.delivered || 0) + (s.read || 0) + (s.queued || 0) + (s.accepted || 0);
  const ko = (s.failed || 0) + (s.undelivered || 0);

  return (
    <div className="space-y-6" data-testid="admin-whatsapp-logs-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Historique WhatsApp</h1>
          <p className="mt-1 text-sm text-slate-500">Toutes les notifications WhatsApp envoyées (commandes, paiements, codes de vérification) avec leur destinataire et leur état.</p>
          <span data-testid="wa-live-indicator" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
            Mise à jour en direct — statut « Livré » et « Lu » en temps réel
          </span>
        </div>
        <div className="flex gap-2">
          <button onClick={sendTest} disabled={testing} data-testid="wa-send-test"
            className="flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyez-moi un message de test
          </button>
          <button onClick={refreshStatuses} disabled={busy} data-testid="wa-refresh-status"
            className="flex items-center gap-2 rounded-full border border-line px-4 py-2.5 text-sm font-bold text-ink hover:border-brand hover:text-brand disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Actualiser les statuts Twilio
          </button>
          <button onClick={clear} data-testid="wa-clear" className="flex items-center gap-2 rounded-full border border-rose-200 px-4 py-2.5 text-sm font-bold text-rose-600 hover:bg-rose-50">
            <Trash2 className="h-4 w-4" /> Effacer
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-4">
        {[
          { label: "Total", value: s.total || 0, icon: MessageCircle, cls: "text-ink" },
          { label: "Envoyés / livrés", value: ok, icon: Send, cls: "text-brand" },
          { label: "Lus", value: s.read || 0, icon: CheckCheck, cls: "text-emerald-600" },
          { label: "Échecs", value: ko, icon: XCircle, cls: "text-rose-600" },
        ].map((k) => (
          <div key={k.label} className="rounded-2xl border border-line bg-white p-5" data-testid={`wa-stat-${k.label.toLowerCase().split(" ")[0]}`}>
            <div className="flex items-center justify-between"><p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{k.label}</p><k.icon className={`h-4 w-4 ${k.cls}`} /></div>
            <p className={`mt-2 font-display text-3xl font-black ${k.cls}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {data && !data.configured && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="wa-not-configured">Twilio n'est pas configuré : les envois sont journalisés comme « Ignoré ».</div>
      )}

      <AdminCard title="Envois" description={`${data?.items?.length || 0} entrée(s) affichée(s) — les plus récentes en premier.`}
        action={
          <div className="flex gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Numéro, texte…" className={`${inputCls} h-10 w-56 pl-9`} data-testid="wa-search" />
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} h-10 w-40`} data-testid="wa-status-filter">
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        }>
        {!data ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        : data.items.length === 0 ? <p className="py-10 text-center text-sm text-slate-400" data-testid="wa-empty">Aucun envoi WhatsApp enregistré pour le moment.</p>
        : (
          <TableWrap>
            <table className="w-full text-left">
              <thead><tr><Th>Date</Th><Th>Destinataire</Th><Th>Type</Th><Th>Message</Th><Th>Statut</Th></tr></thead>
              <tbody className="divide-y divide-line">
                {data.items.map((it) => {
                  const st = STATUS[it.status] || { label: it.status, tone: "slate" };
                  return (
                    <tr key={it.id} className="cursor-pointer hover:bg-surface" onClick={() => setOpen(open === it.id ? null : it.id)} data-testid={`wa-row-${it.id}`}>
                      <Td className="whitespace-nowrap text-xs text-slate-500">{fmt(it.created_at)}</Td>
                      <Td className="font-mono text-xs font-bold">{it.to}</Td>
                      <Td className="text-xs">{it.label}</Td>
                      <Td className={`max-w-md text-xs text-slate-600 ${open === it.id ? "whitespace-pre-wrap" : "truncate"}`}>{it.body}{open === it.id && it.error && <span className="mt-1 block text-rose-600">Erreur : {it.error}</span>}</Td>
                      <Td><Pill tone={st.tone}>{st.label}</Pill></Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
      </AdminCard>
    </div>
  );
}
